import { STORAGE_KEY, LoggerConfig } from './loggerConfig'

/**
 * Generate unique ID for log entry
 * @returns {string} Unique identifier
 */
function generateId() {
    return crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
}

/**
 * Load logs from localStorage
 * @returns {Array} Array of log entries
 */
function loadLogs() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []
    } catch (error) {
        console.warn('Failed to load logs from localStorage:', error)
        return []
    }
}

/**
 * Save logs to localStorage
 * @param {Array} logs - Array of log entries to save
 */
function saveLogs(logs) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(logs))
    } catch (error) {
        console.error('Failed to save logs to localStorage:', error)
    }
}

/**
 * Get caller function name from stack trace
 * @returns {string} Function name or 'unknown'
 */
function getCallerLine() {
    try {
        const stack = new Error().stack?.split('\n') || []
        
        for (const row of stack) {
            if (row.includes('Proxy.')) {
                const match = row.match(/Proxy\.([^(]+)/)
                if (match?.[1]) return match[1]
            }
        }
        
        return 'unknown'
    } catch {
        return 'unknown'
    }
}

/**
 * Storage Logger utility for Vue component logging
 */
export const StorageLogger = {
    /**
     * Log custom message with payload
     * @param {string} message - Log message
     * @param {any} payload - Additional data to log
     * @param {string} component - Component name
     */
    logCustom(message, payload = null, component) {
        if (!LoggerConfig.enabled()) return
        
        const shouldLog = LoggerConfig.logAllComponents || 
                          LoggerConfig.logComponents.includes(component)
        
        if (!shouldLog) return
        
        const dataLog = {
            id: generateId(),
            ts: Date.now(),
            type: 'custom',
            component,
            line: getCallerLine(),
            message,
            payload,
        }
        
        // Save to localStorage
        const logs = loadLogs()
        logs.push(dataLog)
        
        if (logs.length > LoggerConfig.maxEntries) {
            logs.shift()
        }
        
        saveLogs(logs)
        
        // Show in console if enabled
        if (LoggerConfig.showInConsole) {
            this.render([dataLog])
        }
    },

    /**
     * Render logs to console with formatting
     * @param {Array|null} logs - Logs to render, or null to render all
     */
    render(logs = null) {
        const logsToRender = logs || this.getAll()
        
        if (!logsToRender.length) {
            console.log('📝 No logs to display')
            return
        }
        
        logsToRender.forEach((log, index) => {
            console.log(
                `%c#${index + 1} %c${new Date(log.ts).toLocaleTimeString()} %c${log.component} %c@ ${log.line} →`,
                'color: #999',
                'color: #888',
                'color: #42b883; font-weight: bold',
                'color: #888; font-style: italic',
                log.message,
                log.payload
            )
        })
    },

    /**
     * Get all logs from localStorage
     * @returns {Array} All stored log entries
     */
    getAll() {
        return loadLogs()
    },

    /**
     * Clear all logs from localStorage and console
     */
    clear() {
        localStorage.removeItem(STORAGE_KEY)
        console.clear()
        console.log('🧹 Logs cleared')
    },

    /**
     * Enable logging
     */
    enable() {
        LoggerConfig.disableLog = false
        console.log('✅ Logging enabled')
    },

    /**
     * Disable logging
     */
    disable() {
        LoggerConfig.disableLog = true
        console.log('❌ Logging disabled')
    },

    /**
     * Filter and render logs by component name
     * @param {string} name - Component name to filter by
     */
    filterByComponent(name) {
        const filtered = this.getAll().filter(log => log.component === name)
        this.render(filtered)
    }
}
